import { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight, RefreshCw, TriangleAlert, Printer } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { SelectField } from "@/components/admin/fields";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { EmptyState } from "@/components/feedback/EmptyState";
import { toast } from "@/components/feedback/ToastHost";
import { errorMessage } from "@/lib/errors";
import { usePermissions } from "@/hooks/usePermissions";
import {
  generateLoadPlan, getLoadPlan, listVehicleTypes, movePlanItem, plansForDay, regenerateLoadPlan,
  type LoadPlan, type LoadPlanItem, type MovePlanItemPayload, type VehicleType,
} from "@/services/api/loadPlans";
import { itemToTopRect, snap } from "@/lib/geometry";

const MM = 1000;
const SNAP = 50;
const todayIso = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`; };
const addDays = (iso: string, delta: number) => { const d = new Date(`${iso}T00:00:00`); d.setDate(d.getDate()+delta); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`; };
type ViewMode = "top" | "left" | "right";

export function ContainerLoadPlannerPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { has } = usePermissions();
  const [day, setDay] = useState(todayIso());
  const [selLoad, setSelLoad] = useState<number | null>(null);
  const [view, setView] = useState<ViewMode>("top");
  const [selItem, setSelItem] = useState<number | null>(null);
  const [vehicleOverride, setVehicleOverride] = useState("");
  const [doubleStack, setDoubleStack] = useState(false);
  const canWrite = has("loadplans.write");
  const dayQ = useQuery({ queryKey: ["loadplans","for-day",day], queryFn: () => plansForDay(day) });
  const planQ = useQuery({ queryKey: ["loadplan",selLoad], queryFn: () => getLoadPlan(selLoad!), enabled: selLoad !== null });
  const vehicleTypesQ = useQuery({ queryKey: ["vehicle-types","all"], queryFn: () => listVehicleTypes(true) });
  const selSummary = useMemo(() => dayQ.data?.find((d) => d.load_id === selLoad) ?? null, [dayQ.data, selLoad]);
  const plan: LoadPlan | null = planQ.data ?? null;
  useEffect(() => { if (selLoad === null && dayQ.data && dayQ.data.length > 0) setSelLoad(dayQ.data[0].load_id); }, [dayQ.data, selLoad]);
  const items = plan?.items ?? [];
  const vehicle = plan?.vehicle_type ?? null;
  const warnings = plan?.warnings ?? [];
  const selItemData = items.find((i) => i.id === selItem) ?? null;
  const generate = useMutation({ mutationFn: (p: { load_id: number; vehicle_type_id?: number; is_double_stack?: boolean }) => generateLoadPlan(p), onSuccess: () => { toast.success("Generated"); if (selLoad !== null) { void qc.invalidateQueries({ queryKey:["loadplan",selLoad] }); void qc.invalidateQueries({ queryKey:["loadplans","for-day",day] }); } }, onError: (e) => toast.error(errorMessage(e)) });
  const regen = useMutation({ mutationFn: (p: { plan_id: number; load_id: number; vehicle_type_id?: number; is_double_stack?: boolean }) => regenerateLoadPlan(p.plan_id, { load_id: p.load_id, vehicle_type_id: p.vehicle_type_id, is_double_stack: p.is_double_stack }), onSuccess: () => { toast.success("Regenerated"); if (selLoad !== null) void qc.invalidateQueries({ queryKey:["loadplan",selLoad] }); }, onError: (e) => toast.error(errorMessage(e)) });
  const moveMut = useMutation({ mutationFn: (a: { itemId: number; payload: MovePlanItemPayload }) => movePlanItem(plan!.id, a.itemId, a.payload), onSuccess: () => { if (selLoad !== null) void qc.invalidateQueries({ queryKey:["loadplan",selLoad] }); }, onError: (e) => { toast.error(errorMessage(e)); if (selLoad !== null) void qc.invalidateQueries({ queryKey:["loadplan",selLoad] }); } });
  const onMove = useCallback((itemId: number, x: number, y: number, deck: number, ori: number) => { if (!plan) return; moveMut.mutate({ itemId, payload: { pos_x_mm: snap(x,SNAP), pos_y_mm: snap(y,SNAP), deck, orientation: ori, row_version: plan.row_version } }); }, [plan, moveMut]);
  const onGen = () => { if (selLoad !== null) generate.mutate({ load_id: selLoad, vehicle_type_id: vehicleOverride ? Number(vehicleOverride) : undefined, is_double_stack: doubleStack ? true : undefined }); };
  const onRegen = () => { if (selLoad !== null && plan) regen.mutate({ plan_id: plan.id, load_id: selLoad, vehicle_type_id: vehicleOverride ? Number(vehicleOverride) : undefined, is_double_stack: doubleStack ? true : undefined }); };
  // Effective vehicle for validation feedback: the override, else the resolved one.
  const effectiveVehicle: VehicleType | null = vehicleOverride
    ? vehicleTypesQ.data?.find((v) => v.id === Number(vehicleOverride)) ?? vehicle
    : vehicle;
  const doubleStackBlocked = doubleStack && effectiveVehicle !== null && !effectiveVehicle.is_double_deck;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-2xl font-semibold">{t("nav.containerLoadPlanner")}</h1>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => setDay(addDays(day,-1))} aria-label="prev"><ChevronLeft className="h-4 w-4"/></Button>
          <Input type="date" value={day} onChange={(e) => setDay(e.target.value || todayIso())} className="w-44"/>
          <Button variant="outline" size="icon" onClick={() => setDay(addDays(day,1))} aria-label="next"><ChevronRight className="h-4 w-4"/></Button>
          <Button variant="outline" size="icon" onClick={() => window.print()} aria-label="print"><Printer className="h-4 w-4"/></Button>
        </div>
      </div>

      {warnings.filter((w) => w.severity === "error").length > 0 && (
        <div className="flex items-start gap-2 rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
          <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
          <div className="space-y-1">{warnings.filter((w) => w.severity === "error").map((w) => (<div key={w.code}>{t(w.message_key, w.params)}</div>))}</div>
        </div>
      )}
      {warnings.filter((w) => w.severity === "warning").length > 0 && (
        <div className="flex items-start gap-2 rounded-md border border-amber-500/50 bg-amber-500/10 p-3 text-sm text-amber-700 dark:text-amber-400">
          <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
          <div className="space-y-1">{warnings.filter((w) => w.severity === "warning").map((w) => (<div key={w.code}>{t(w.message_key, w.params)}</div>))}</div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[280px_1fr_300px]">

        <div className="space-y-3">
          <div className="rounded-md border p-3">
            <h2 className="mb-2 text-sm font-semibold">{t("loadplan.dayLoads")}</h2>
            {dayQ.isLoading ? <div className="text-sm text-muted-foreground">{t("common.loading")}</div>
              : dayQ.data && dayQ.data.length > 0 ? (
                <div className="space-y-1">{dayQ.data.map((s) => (
                  <button key={s.load_id} onClick={() => { setSelLoad(s.load_id); setSelItem(null); }}
                    className={`flex w-full items-center justify-between rounded px-2 py-1.5 text-left text-sm ${selLoad === s.load_id ? "bg-primary/10 font-medium text-primary" : "hover:bg-muted"}`}>
                    <span className="truncate">{s.load_ref}</span>
                    {s.has_error && <TriangleAlert className="h-3.5 w-3.5 shrink-0 text-destructive"/>}
                  </button>
                ))}</div>
              ) : <EmptyState title={t("loadplan.noLoads")}/>}
          </div>
          {selLoad !== null && (
            <div className="rounded-md border p-3">
              <h2 className="mb-2 text-sm font-semibold">{t("loadplan.vehicle")}</h2>
              <div className="space-y-1 text-sm">
                {vehicle ? (
                  <>
                    <div className="font-medium">{vehicle.name}</div>
                    <div className="text-muted-foreground">{vehicle.internal_length_mm/MM} × {vehicle.internal_width_mm/MM} m{vehicle.is_double_deck ? ` · ${t("loadplan.doubleDeck")}` : ""}</div>
                  </>
                ) : (
                  // No plan yet: the vehicle is resolved server-side on generate.
                  <div className="text-muted-foreground">{t("loadplan.resolvedVehicle")}</div>
                )}
                {selSummary && <div className="text-muted-foreground">{selSummary.load_ref}</div>}
              </div>
              {canWrite && (
                <div className="mt-3 flex flex-col gap-2">
                  <SelectField
                    label={t("loadplan.vehicleOverride")}
                    value={vehicleOverride}
                    onChange={setVehicleOverride}
                    options={[
                      {
                        value: "",
                        label: vehicle
                          ? `${t("loadplan.resolvedVehicle")}: ${vehicle.name}`
                          : t("loadplan.resolvedVehicle"),
                      },
                      ...(vehicleTypesQ.data ?? [])
                        .filter((v) => v.is_active)
                        .map((v) => ({ value: v.id.toString(), label: `${v.code} — ${v.name}` })),
                    ]}
                  />
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox checked={doubleStack} onCheckedChange={(v) => setDoubleStack(Boolean(v))} />
                    {t("loadplan.doubleStack")}
                  </label>
                  {doubleStack && (
                    <p className={`text-xs ${doubleStackBlocked ? "text-destructive" : "text-muted-foreground"}`}>
                      {t("loadplan.doubleStackHint")}
                    </p>
                  )}
                  <Button size="sm" onClick={onGen} disabled={generate.isPending || selLoad === null}>{t("loadplan.generate")}</Button>
                  {plan !== null && (
                    <Button size="sm" variant="outline" onClick={onRegen} disabled={regen.isPending}><RefreshCw className="mr-1 h-3 w-3"/> {t("loadplan.regenerate")}</Button>
                  )}
                </div>
              )}
            </div>
          )}
          {plan && (
            <div className="rounded-md border p-3">
              <h2 className="mb-2 text-sm font-semibold">{t("loadplan.legend")}</h2>
              <div className="space-y-1">{Array.from(new Set(plan.items.map((i) => i.site_color))).map((c) => (
                <div key={c ?? "none"} className="flex items-center gap-2 text-sm">
                  <span className="inline-block h-3 w-3 rounded border" style={{ backgroundColor: c ?? "var(--color-muted-foreground)" }}/>
                  <span className="truncate">{plan.items.find((i) => i.site_color === c)?.site_name ?? t("loadplan.unknownSite")}</span>
                </div>
              ))}</div>
            </div>
          )}
        </div>

        <div className="rounded-md border">
          <div className="flex items-center gap-1 border-b p-2">
            {(["top","left","right"] as ViewMode[]).map((m) => (<Button key={m} size="sm" variant={view===m?"default":"outline"} onClick={() => setView(m)}>{t(`loadplan.view.${m}`)}</Button>))}
          </div>
          <div className="p-3">
            {plan === null ? (
              <div className="flex h-96 flex-col items-center justify-center gap-3 px-6 text-center text-sm text-muted-foreground">
                {selLoad === null ? (
                  t("loadplan.selectLoad")
                ) : planQ.isLoading ? (
                  t("common.loading")
                ) : (
                  <>
                    {/* One inline wrapper so flex layout cannot strip the space
                        between "the" and the linked "ledger" word. */}
                    <span>
                      <Trans
                        i18nKey="loadplan.noPlan"
                        defaults="No plan yet — generate one from the <0>ledger</0>, or the load has no collection legs."
                        components={[
                          has("loads.read") ? (
                            <Link
                              to="/admin/loads"
                              className="font-medium text-primary underline underline-offset-2 hover:no-underline"
                            >
                              ledger
                            </Link>
                          ) : (
                            <span>ledger</span>
                          ),
                        ]}
                      />
                    </span>
                    {canWrite && (
                      <Button size="sm" onClick={onGen} disabled={generate.isPending}>
                        {t("loadplan.generate")}
                      </Button>
                    )}
                  </>
                )}
              </div>
            ) : <VehicleCanvas items={items} vehicle={vehicle!} view={view} selectedItemId={selItem} canMove={canWrite} onSelectItem={setSelItem} onMoveItem={onMove}/>}
          </div>
        </div>

        <div className="space-y-3">
          {selItemData ? <ItemInspector item={selItemData} vehicle={vehicle} items={items} canMove={canWrite} onMove={onMove}/>
            : <div className="rounded-md border p-3 text-sm text-muted-foreground">{t("loadplan.selectItem")}</div>}
          {warnings.length > 0 && (
            <div className="rounded-md border p-3">
              <h2 className="mb-2 text-sm font-semibold">{t("loadplan.warnings")}</h2>
              <div className="space-y-1">{warnings.map((w) => (<div key={w.code} className={`text-sm ${w.severity==="error"?"text-destructive":"text-amber-600 dark:text-amber-400"}`}>{t(w.message_key, w.params)}</div>))}</div>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}

interface CanvasProps {
  items: LoadPlanItem[];
  vehicle: VehicleType;
  view: ViewMode;
  selectedItemId: number | null;
  canMove: boolean;
  onSelectItem: (id: number | null) => void;
  onMoveItem: (itemId: number, x: number, y: number, deck: number, ori: number) => void;
}

function VehicleCanvas({ items, vehicle, view, selectedItemId, canMove, onSelectItem, onMoveItem }: CanvasProps) {
  const { t } = useTranslation();
  const [drag, setDrag] = useState<{ itemId: number; dx: number; dy: number } | null>(null);
  const lengthMm = vehicle.internal_length_mm;
  const spanMm = view === "top" ? vehicle.internal_width_mm : vehicle.internal_height_mm;
  const canvasW = 900;
  const canvasH = 360;
  const padX = 50;
  const padY = 30;
  const scale = Math.min((canvasW - padX * 2) / lengthMm, (canvasH - padY * 2) / spanMm);
  const toX = (mm: number) => padX + mm * scale;
  const toY = (mm: number) => padY + mm * scale;

  const onPointerDown = (e: React.PointerEvent, item: LoadPlanItem) => {
    if (!canMove) { onSelectItem(item.id); return; }
    e.preventDefault();
    (e.target as Element).setPointerCapture?.(e.pointerId);
    setDrag({ itemId: item.id, dx: 0, dy: 0 });
    onSelectItem(item.id);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!drag) return;
    const rect = (e.currentTarget as Element).getBoundingClientRect();
    const item = items.find((i) => i.id === drag.itemId);
    if (!item) return;
    setDrag({ ...drag, dx: e.clientX - rect.left - toX(item.pos_x_mm), dy: e.clientY - rect.top - toY(item.pos_y_mm) });
  };

  const onPointerUp = () => {
    if (!drag) return;
    const item = items.find((i) => i.id === drag.itemId);
    if (item) {
      const fp = itemToTopRect(0, 0, item.length_mm, item.width_mm, item.orientation);
      const newX = item.pos_x_mm + drag.dx / scale;
      const newY = item.pos_y_mm + drag.dy / scale;
      onMoveItem(item.id, Math.min(Math.max(0, lengthMm - fp.width), Math.max(0, newX)), Math.min(Math.max(0, spanMm - fp.height), Math.max(0, newY)), item.deck, item.orientation);
    }
    setDrag(null);
  };

  return (
    <div className="space-y-2">
      <svg width={canvasW} height={canvasH} className="w-full rounded border bg-muted/20" onPointerMove={onPointerMove} onPointerUp={onPointerUp}>
        <rect x={toX(0)} y={toY(0)} width={lengthMm * scale} height={spanMm * scale} fill="var(--color-card)" stroke="var(--color-border)" strokeWidth={2} />
        <text x={toX(0) + 4} y={toY(0) + 14} className="fill-muted-foreground" fontSize={11}>Bulkhead</text>
        <text x={toX(lengthMm) - 40} y={toY(0) + 14} className="fill-muted-foreground" fontSize={11}>Doors</text>
        {vehicle.is_double_deck && vehicle.deck2_clearance_mm !== null && view !== "top" && (
          <line x1={toX(0)} y1={toY(vehicle.internal_height_mm - vehicle.deck2_clearance_mm)} x2={toX(lengthMm)} y2={toY(vehicle.internal_height_mm - vehicle.deck2_clearance_mm)} stroke="var(--color-border)" strokeDasharray="4 3" />
        )}
        {items.map((item) => {
          const fp = itemToTopRect(item.pos_x_mm, item.pos_y_mm, item.length_mm, item.width_mm, item.orientation);
          const dragging = drag?.itemId === item.id;
          const x = toX(item.pos_x_mm + (dragging ? drag.dx / scale : 0));
          const y = toY(item.pos_y_mm + (dragging ? drag.dy / scale : 0));
          const w = (view === "top" ? fp.width : item.length_mm) * scale;
          const h = (view === "top" ? fp.height : item.height_mm) * scale;
          const selected = item.id === selectedItemId;
          return (
            <g key={item.id} onPointerDown={(e) => onPointerDown(e, item)} style={{ cursor: canMove ? "grab" : "pointer" }}>
              <rect x={x} y={y} width={w} height={h} fill={item.site_color ?? "var(--color-muted-foreground)"} stroke={selected ? "var(--color-primary)" : "var(--color-card-foreground)"} strokeWidth={selected ? 2 : 1} opacity={item.deck === 2 ? 0.85 : 1} />
              {item.stop_sequence !== null && <text x={x + w / 2} y={y + h / 2 + 4} textAnchor="middle" className="fill-white" fontSize={11} fontWeight={600}>{item.stop_sequence}</text>}
            </g>
          );
        })}
      </svg>
      <div className="text-xs text-muted-foreground">{t("loadplan.dragHint")}</div>
    </div>
  );
}

interface InspectorProps {
  item: LoadPlanItem;
  vehicle: VehicleType | null;
  items: LoadPlanItem[];
  canMove: boolean;
  onMove: (itemId: number, x: number, y: number, deck: number, ori: number) => void;
}

function ItemInspector({ item, vehicle, canMove, onMove }: InspectorProps) {
  const { t } = useTranslation();
  const [deck, setDeck] = useState(item.deck);
  const [orientation, setOrientation] = useState(item.orientation);
  useEffect(() => { setDeck(item.deck); setOrientation(item.orientation); }, [item.deck, item.orientation]);
  return (
    <div className="rounded-md border p-3">
      <h2 className="mb-2 text-sm font-semibold">{t("loadplan.item")}</h2>
      <div className="space-y-2 text-sm">
        <div><span className="text-muted-foreground">{t("loadplan.loadRef")}:</span> {item.load_ref ?? "—"}</div>
        <div><span className="text-muted-foreground">{t("loadplan.site")}:</span> {item.site_name ?? t("loadplan.unknownSite")}</div>
        <div><span className="text-muted-foreground">{t("loadplan.stop")}:</span> {item.stop_sequence ?? "—"}</div>
        <div><span className="text-muted-foreground">{t("loadplan.dimensions")}:</span> {item.length_mm}×{item.width_mm}×{item.height_mm} mm</div>
        <div><span className="text-muted-foreground">{t("loadplan.weight")}:</span> {item.weight_kg !== null ? `${item.weight_kg} kg` : "—"}</div>
        {canMove && vehicle && (
          <>
            <div className="flex items-center gap-2 pt-2">
              <Label className="w-16 text-xs">{t("loadplan.deck")}</Label>
              <Select value={String(deck)} onValueChange={(v) => setDeck(Number(v))}><SelectTrigger className="w-24"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="1">1</SelectItem>{vehicle.is_double_deck && <SelectItem value="2">2</SelectItem>}</SelectContent></Select>
            </div>
            <div className="flex items-center gap-2">
              <Label className="w-16 text-xs">{t("loadplan.orientation")}</Label>
              <Select value={String(orientation)} onValueChange={(v) => setOrientation(Number(v))}><SelectTrigger className="w-24"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="0">0°</SelectItem><SelectItem value="90">90°</SelectItem></SelectContent></Select>
            </div>
            <Button size="sm" onClick={() => onMove(item.id, item.pos_x_mm, item.pos_y_mm, deck, orientation)} className="w-full">{t("loadplan.apply")}</Button>
          </>
        )}
      </div>
    </div>
  );
}
